import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

type Lead = {
  _id: string;
  email: string;
  phone?: string;
  monthlyRevenue: number;
  adminHoursPerWeek: number;
  estimates: {
    vsMarketplaceAnnual: number;
    vsSaasSubscriptionsAnnual: number;
    vsSpreadsheetsTimeValueAnnual: number;
    hoursSavedPerMonth: number;
  };
  createdAt: string;
};

const currency = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(n || 0));

const formatPhone = (digits?: string) => {
  if (!digits || digits.length !== 10) return digits || '—';
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const AdminProviderSavingsLeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await apiService.get('/leads/provider-savings');
        if (res.success) {
          setLeads((res.data as Lead[]) || []);
        } else {
          setError((res as any).error || 'Failed to load leads');
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load leads');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
        Back to Admin
      </Button>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
        Provider Savings Leads
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Emails and optional phones from the homepage Provider Savings Snapshot. You also get an email
        notification when each lead submits.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && leads.length === 0 && (
        <Alert severity="info">No savings leads yet. They’ll appear here after someone unlocks results.</Alert>
      )}

      {!loading && leads.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Submitted</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="right">Monthly revenue</TableCell>
                <TableCell align="right">Admin hrs/wk</TableCell>
                <TableCell align="right">vs Marketplaces</TableCell>
                <TableCell align="right">vs SaaS</TableCell>
                <TableCell align="right">vs Spreadsheets</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.map((lead) => (
                <TableRow key={lead._id} hover>
                  <TableCell>{new Date(lead.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <a href={`mailto:${lead.email}`}>{lead.email}</a>
                  </TableCell>
                  <TableCell>
                    {lead.phone ? (
                      <a href={`tel:+1${lead.phone}`}>{formatPhone(lead.phone)}</a>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">{currency(lead.monthlyRevenue)}</TableCell>
                  <TableCell align="right">{lead.adminHoursPerWeek}</TableCell>
                  <TableCell align="right">{currency(lead.estimates?.vsMarketplaceAnnual)}</TableCell>
                  <TableCell align="right">{currency((lead.estimates?.vsSaasSubscriptionsAnnual || 0) / 12)}/mo</TableCell>
                  <TableCell align="right">{currency(lead.estimates?.vsSpreadsheetsTimeValueAnnual)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default AdminProviderSavingsLeadsPage;
